import Divider from "@material-ui/core/Divider";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import React, { FC, useState } from "react";
import { Auth } from "aws-amplify";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";

import ConfirmationModal from "../../../modules/ConfirmationModal/ConfirmationModal";
import ProfileAdvertiserSelector from "./ProfileAdvertiserSelector";
import { ROUTE_PATHS } from "../../../../types/RoutePaths";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { resetAppState } from "../../../../redux/reducers/reset";
import { useGlobalSelectedAdvertiser } from "../../../../hooks/useGlobalSelectedAdvertiser";

type TopBarProps = {
  title: string;
  subtitle?: string;
};

const PROFILE_MENU_ITEMS = [
  { label: "Manage Advertisers", path: ROUTE_PATHS.ADVERTISERS_LIST },
  { label: "Terms of Use", path: ROUTE_PATHS.TERMS_PAGE },
  { label: "Privacy Policy", path: ROUTE_PATHS.PRIVACY_PAGE },
] as const;

const getAgencyInitials = (agencyName: string): string => {
  const words = agencyName.trim().split(/\s+/).filter(Boolean);

  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return "?";
};

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 32px",
  background: theme.palette.brand.white,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  minHeight: 64,
  flexShrink: 0,
}));

const Title = styled("h1")(({ theme }) => ({
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.brand.gray900,
  letterSpacing: "-0.01em",
}));

const Subtitle = styled("div")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  marginTop: 2,
}));

const RightGroup = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
});

const ProfileButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 10px 4px 4px",
  borderRadius: 8,
  background: "transparent",
  border: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  fontFamily: "inherit",
  width: 240,
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
}));

const AvatarCircle = styled("span")(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: theme.palette.brand.navy,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.brand.white,
  fontSize: 11,
  fontWeight: 600,
  flexShrink: 0,
}));

const ProfileButtonLabel = styled("span")(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  flex: 1,
  textAlign: "left",
}));

const ProfileButtonChevron = styled(ExpandMoreIcon)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.brand.gray500,
  flexShrink: 0,
}));

const ProfileMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: 8,
    minWidth: 240,
    borderRadius: 10,
    border: `1px solid ${theme.palette.brand.gray200}`,
    boxShadow: "0 8px 24px rgba(15, 43, 76, 0.12)",
  },
}));

const ProfileMenuDivider = styled(Divider)(({ theme }) => ({
  margin: "4px 0",
  backgroundColor: theme.palette.brand.gray200,
}));

const ProfileMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray700,
  padding: "10px 16px",
  "&:hover": {
    background: theme.palette.brand.gray50,
    color: theme.palette.brand.gray900,
  },
}));

const LogoutMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray700,
  padding: "10px 16px",
  "& .logout-icon": {
    marginRight: 8,
    fontSize: 18,
    color: theme.palette.brand.gray500,
    transition: "color 0.15s",
  },
  "&:hover": {
    background: "#fff5f5",
    color: "#c0392b",
    "& .logout-icon": {
      color: "#c0392b",
    },
  },
}));

const TopBar: FC<TopBarProps> = ({ title, subtitle }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const agencyName = useSelector((state: ReduxApplicationState) => state.agency.name);
  const { selectedAdvertiser } = useGlobalSelectedAdvertiser();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const isProfileMenuOpen = Boolean(menuAnchorEl);

  const isAdvertisersPage = useRouteMatch({
    path: "/advertisers/:id?",
    exact: true,
  });

  const handleLogout = async () => {
    await Auth.signOut();
    localStorage.clear();
    sessionStorage.clear();
    dispatch(resetAppState());
    history.push("/");
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    history.push(path);
    handleProfileMenuClose();
  };

  return (
    <Root>
      <div>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </div>

      <RightGroup>
        <ProfileButton
          type="button"
          aria-label="Open profile menu"
          aria-haspopup="true"
          aria-expanded={isProfileMenuOpen}
          onClick={handleProfileClick}
        >
          <AvatarCircle>{getAgencyInitials(agencyName)}</AvatarCircle>
          <ProfileButtonLabel>
            {isAdvertisersPage ? agencyName : selectedAdvertiser?.name ?? agencyName}
          </ProfileButtonLabel>
          <ProfileButtonChevron />
        </ProfileButton>

        <ProfileMenu
          anchorEl={menuAnchorEl}
          open={isProfileMenuOpen}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          getContentAnchorEl={null}
        >
          {!isAdvertisersPage && <ProfileAdvertiserSelector onAdvertiserSelected={handleProfileMenuClose} />}
          {PROFILE_MENU_ITEMS.map((item) => (
            <ProfileMenuItem key={item.path} onClick={() => handleMenuItemClick(item.path)}>
              {item.label}
            </ProfileMenuItem>
          ))}
          <ProfileMenuDivider />
          <LogoutMenuItem
            onClick={() => {
              handleProfileMenuClose();
              setIsLogoutModalOpen(true);
            }}
          >
            <ExitToAppIcon className="logout-icon" />
            Log out
          </LogoutMenuItem>
        </ProfileMenu>

        <ConfirmationModal
          isOpen={isLogoutModalOpen}
          confirmQuestion="Log out from BrandGen?"
          confirmButtonLabel="LOG OUT"
          handleClose={() => setIsLogoutModalOpen(false)}
          handleConfirm={handleLogout}
        />
      </RightGroup>
    </Root>
  );
};

export default TopBar;
